import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';

import { postGlobalAssistantSummarizeConversation } from './global-ai-assistant-api';
import {
  type GlobalAiConversationSummary,
  RECENT_TURNS_WINDOW,
  SUMMARIZE_TRIGGER,
  SUMMARY_REGEN_DELTA,
  transcriptToGlobalAssistantConversationTurns,
} from './global-ai-assistant-conversation';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';

const SUMMARIZER_DEBOUNCE_MS = 1500;

/**
 * Watches the transcript for turns rolling out of the recency window
 * and asynchronously generates a compact summary of those older turns.
 *
 * The summary lives on the assistant context state and is threaded into
 * every backend request so prompts (router, intent generation, AI
 * Search filter extraction, page-stream create / update) can interpret
 * short follow-ups even after a long session.
 *
 * Trigger: schedule a summary when `transcript.length - RECENT_TURNS_WINDOW`
 * is at least `SUMMARIZE_TRIGGER` and either no summary exists yet OR
 * `SUMMARY_REGEN_DELTA` more turns have rolled out since the last
 * summary's `upToTurnIndex`.
 *
 * The fetch is debounced (waits for the transcript to settle) and
 * aborted if the transcript changes again before completion. The hook
 * silently swallows errors so a flaky summarizer never blocks the user.
 */
export function useGlobalAiConversationSummarizer(args: {
  transcript: GlobalAiTranscriptMessage[];
  summary: GlobalAiConversationSummary | null;
  setSummary: Dispatch<SetStateAction<GlobalAiConversationSummary | null>>;
}) {
  const { transcript, summary, setSummary } = args;
  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic counter so a late-resolving fetch can never overwrite a
  // newer summary in flight (belt-and-braces alongside the abort
  // signal — guards against any pathological resolution that bypasses
  // the abort).
  const generationRef = useRef(0);

  useEffect(() => {
    const oldTurnsCount = transcript.length - RECENT_TURNS_WINDOW;
    const shouldRegenerate = (() => {
      if (oldTurnsCount < SUMMARIZE_TRIGGER) {
        return false;
      }
      if (!summary) {
        return true;
      }
      return oldTurnsCount - summary.upToTurnIndex >= SUMMARY_REGEN_DELTA;
    })();

    if (!shouldRegenerate) {
      return;
    }

    // Snapshot the slice of transcript to summarize so a subsequent
    // append doesn't shift our `upToTurnIndex`.
    const olderSlice = transcript.slice(0, oldTurnsCount);
    const turns = transcriptToGlobalAssistantConversationTurns(olderSlice);
    if (turns.length === 0) {
      return;
    }

    const ac = new AbortController();
    const myGeneration = ++generationRef.current;
    const handle = setTimeout(() => {
      abortRef.current?.abort();
      abortRef.current = ac;
      void postGlobalAssistantSummarizeConversation({ turns }, ac.signal)
        .then((res) => {
          if (myGeneration !== generationRef.current) {
            return;
          }
          const text = res.summary?.trim();
          if (!text) {
            return;
          }
          setSummary({
            upToTurnIndex: oldTurnsCount,
            text,
            generatedAtMs: Date.now(),
          });
        })
        .catch(() => {
          // Summarizer is best-effort. If it fails, requests will still
          // include the recent window verbatim.
        });
    }, SUMMARIZER_DEBOUNCE_MS);
    timeoutRef.current = handle;

    return () => {
      clearTimeout(handle);
      ac.abort();
    };
  }, [transcript, summary, setSummary]);

  // Cancel any in-flight work on unmount.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      abortRef.current?.abort();
    };
  }, []);
}
